import { memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import { IconCheck, IconHelpCircle } from '@u21/tabler-icons';

import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import {
  U21AiChatBubbleInnerProps,
  U21AiChatBubbleType,
} from 'app/shared/u21-ui/components/input/chat/shared';
import { U21AiChatMultiSelectInner } from 'app/shared/u21-ui/components/input/chat/U21AiChatMultiSelectInner';
import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';

type U21AiChatBubbleMultiSelectProps = U21AiChatBubbleInnerProps & {
  type: U21AiChatBubbleType.MULTISELECT;
  values: string[];
  onSubmit: (answer: string[]) => void;
};

export type U21AiChatBubbleProps = {
  question: string;
  readonly?: boolean;
} & U21AiChatBubbleMultiSelectProps;

type RenderInnerProps = {
  disabled?: boolean;
  onChange: (answer: string[]) => void; // This is intentional since answers is always string[] for now
} & {
  type: U21AiChatBubbleType.MULTISELECT;
  choices: string[];
  initialValues: string[];
};

const renderInner = (props: RenderInnerProps) => {
  switch (props.type) {
    case U21AiChatBubbleType.MULTISELECT:
      return (
        <U21AiChatMultiSelectInner
          choices={props.choices}
          disabled={props.disabled}
          initialValues={props.initialValues}
          onChange={props.onChange}
        />
      );
    default:
      return null;
  }
};

export const U21AiChatBubble = memo(
  ({
    type,
    question,
    choices,
    disabled,
    readonly,
    values,
    onSubmit,
  }: U21AiChatBubbleProps) => {
    // Internally we are using string[] to handle the different values. Might be updated to use specific values types down the line. (e.g. date[], number[], etc.)
    const [answers, setAnswers] = useState<string[]>([]);

    const hasValue = useMemo(() => answers.length > 0, [answers]);

    if (readonly) {
      const selectedValues = values ?? [];
      return (
        <BubbleCard>
          <U21Spacer spacing={1.5}>
            <U21Spacer horizontal align="start">
              <StyledIconHelpCircle size={18} $greyed />
              <U21Typography variant="subtitle2" color="text.secondary">
                {question}
              </U21Typography>
            </U21Spacer>

            {selectedValues.length > 0 && (
              <U21Spacer horizontal align="start">
                <StyledIconCheck />
                <U21Typography variant="subtitle2">
                  {new Intl.ListFormat('en', {
                    style: 'long',
                    type: 'conjunction',
                  }).format(selectedValues)}
                </U21Typography>
              </U21Spacer>
            )}
          </U21Spacer>
        </BubbleCard>
      );
    }

    return (
      <BubbleCard>
        <U21Spacer spacing={1.5}>
          <U21Spacer horizontal align="start">
            <StyledIconHelpCircle size={18} />
            <U21Typography variant="subtitle2">{question}</U21Typography>
          </U21Spacer>

          {renderInner({
            type,
            choices,
            disabled,
            initialValues: values,
            onChange: setAnswers,
          })}

          <StyledSubmitButton
            color="primary"
            variant="contained"
            size="small"
            onClick={() => onSubmit(answers)}
            disabled={disabled || !hasValue}
          >
            Submit
          </StyledSubmitButton>
        </U21Spacer>
      </BubbleCard>
    );
  },
);

const BubbleCard = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-left: 4px solid ${({ theme }) => theme.palette.primary.main};
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 ${({ theme }) => theme.shape.borderRadius}px
    ${({ theme }) => theme.shape.borderRadius}px 0;
`;

const StyledIconHelpCircle = styled(IconHelpCircle)<{ $greyed?: boolean }>`
  color: ${({ theme, $greyed }) =>
    $greyed ? theme.palette.text.secondary : theme.palette.info.main};
  flex-shrink: 0;
`;

const StyledIconCheck = styled(IconCheck)`
  color: ${({ theme }) => theme.palette.primary.main};
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const StyledSubmitButton = styled(U21Button)`
  width: fit-content;
`;
